import { put, select, putResolve, call } from "redux-saga/effects";

// actions
import {
  ICategorizedResponse,
  AirMultiCityEntryProperties,
  AIR_MULTICITY_ENTRY,
  ILocationQueryLabel,
  IResponse,
  ITripTerminus,
  LocationQueryEnum,
  SliceStopCountFilter,
  AirEntryProperties,
  AIR_ENTRY,
  ShopFilter,
  Platform,
  TripCategory,
} from "redmond";
import {
  ShopMulticitySummaryRequest,
  ShopMulticitySummaryResponse,
  ShopMulticitySummarySuccessResultEnum,
} from "@b2bportal/air-shopping-api";
import dayjs from "dayjs";

import { actions as searchActions } from "../../search/actions";
import { actions } from "../actions";

// reducers
import {
  getAirEntryProperties,
  getMultiCityAirEntryProperties,
  MulticityFlightShopStep,
} from "../reducer";

// types
import { ITripTerminusCategory } from "../../search/types";
import { MulticityRoute, MobileFlightSearchStep } from "../../search/reducer";
import { IStoreState } from "../../../reducers/types";

// helpers, utils
import {
  getExistingStateVariables,
  populateFlightShopMulticityQueryParametersFromState,
} from "./populateShopQueryParamsSaga";
import { PATH_BOOK, PATH_HOME } from "../../../utils/urlPaths";
import Logger from "../../../helpers/Logger";
import {
  getMulticityDepartureStringValuesFromParsedShopQuery,
  IMulticityFlightShopParsedQuery,
  parseQueryString,
} from "../utils/parseQueryString";

// api
import { trackEvent } from "../../../api/v0/analytics/trackEvent";
import { fetchLocationAutocomplete } from "../../../api/v0/search/fetchLocationAutocomplete";
import { fetchMulticityTripSummaries as fetchFlights } from "../../../api/v0/shop/fetchMulticityTripSummaries";

const shouldRedirect = ({
  multicityRoutes,
  stopsOption,
}: {
  multicityRoutes: any[];
  stopsOption: SliceStopCountFilter;
}): boolean => {
  const isAtLeast2Routes = multicityRoutes.length >= 2;
  const isAllDefinedMulticityFieldsPopulated = multicityRoutes.every((r) => {
    const allFields = Object.values(r);
    return allFields.every((field) => field !== null);
  });

  return (
    !stopsOption || !isAtLeast2Routes || !isAllDefinedMulticityFieldsPopulated
  );
};

export function* fetchMulticityTripSummaries(
  action: actions.IFetchMulticityTripSummaries,
) {
  try {
    const {
      routes,
      adultsCount,
      childrenCount,
      infantsInSeatCount,
      infantsOnLapCount,
      stopsOption,
      noLCC,
    } = yield call(setUpMulticityFlightShopParams, action);

    const passengerObj: { [key: string]: number } = {};

    if (adultsCount > 0) passengerObj.ADT = adultsCount;
    if (infantsInSeatCount > 0) passengerObj.INS = infantsInSeatCount;
    if (infantsOnLapCount > 0) passengerObj.INF = infantsOnLapCount;
    if (childrenCount > 0) passengerObj.CNN = childrenCount;

    let tripFilter = ShopFilter.NoFilter;
    const filterOutBasicFares = noLCC;

    if (filterOutBasicFares) {
      if (stopsOption === SliceStopCountFilter.NONE) {
        tripFilter = ShopFilter.NonStopNoLCC;
      } else {
        tripFilter = ShopFilter.NoLCC;
      }
    } else if (stopsOption === SliceStopCountFilter.NONE) {
      tripFilter = ShopFilter.NonStop;
    }

    const requestBody: ShopMulticitySummaryRequest = {
      passengers: passengerObj,
      routes,
      tripFilter,
      platform: action.isMobile ? Platform.Mobile : Platform.Desktop,
    };

    const response: ShopMulticitySummaryResponse = yield fetchFlights(
      requestBody,
    );

    yield putResolve(actions.setShopRequest(requestBody));

    const airEntryProperties: AirEntryProperties | null = yield select(
      getAirEntryProperties,
    );
    yield trackEvent({
      eventName: AIR_ENTRY,
      properties: {
        ...airEntryProperties,
      },
    });

    const multiCityAirEntryProperties: AirMultiCityEntryProperties | null =
      yield select(getMultiCityAirEntryProperties);
    yield trackEvent({
      eventName: AIR_MULTICITY_ENTRY,
      properties: multiCityAirEntryProperties,
    });

    if (response.result.Result === ShopMulticitySummarySuccessResultEnum.Ok) {
      yield putResolve(
        actions.setMulticityFlights(response.result.value.flights),
      );
    } else {
      yield putResolve(actions.setMulticityFlights([]));
      yield putResolve(
        actions.setTripSummariesError(
          response.result.err.ShopMulticitySummaryErrors,
        ),
      );
    }

    yield put(searchActions.setAwaitingRefetch(false));
  } catch (e) {
    const errorCode: string = (e as any)[0]?.code;
    yield put(actions.setTripSummariesError(errorCode));
    Logger.debug(e);
  }
}

export function* setUpMulticityFlightShopParams(
  action: actions.IFetchMulticityTripSummaries,
) {
  let state: IStoreState = yield select();
  const { history } = action;

  const {
    multicityFlightShopProgress,
    multicityRoutes,
    tripCategory,
    stopsOption: stopsOptionFromState,
    noLCC: noLCCFromState,
    fareClass: fareClassFromState,
  } = getExistingStateVariables(state);

  const isAllFieldsPopulated = (routes: MulticityRoute[]) =>
    routes.every((r) => Object.values(r).every((field) => field !== null));

  // The b2bportal search form hands the multicity shop its itinerary via plain
  // URL params (it no longer dispatches into Redux). Because the legacy store
  // persists across SPA navigation, a second multicity search in the same session
  // arrives with a fresh URL while Redux may still hold the *previous* fully
  // populated itinerary — so Redux completeness is the wrong signal for "is this a
  // fresh handoff" (it would skip hydration and let the stale store overwrite the
  // new URL). The URL is the source of truth: always hydrate the cheap fields
  // (pax / stops / fareClass) from it, and re-resolve routes via the autocomplete
  // lookup only when the URL legs differ from state (or state is incomplete), so a
  // no-op re-entry with a matching URL doesn't re-fetch data already in state.
  const parsedQueryString = parseQueryString(
    history,
  ) as IMulticityFlightShopParsedQuery;

  const urlLegs =
    getMulticityDepartureStringValuesFromParsedShopQuery(parsedQueryString);
  const hasUrlItinerary = urlLegs.length >= 2;

  const stateLegs = multicityRoutes.map((route) => ({
    origin: route.origin?.id.code.code,
    destination: route.destination?.id.code.code,
    departureDate: route.departureDate
      ? dayjs(route.departureDate).format("YYYY-MM-DD")
      : undefined,
  }));
  const urlLegsMatchState =
    urlLegs.length === stateLegs.length &&
    urlLegs.every(
      (leg, index) =>
        leg.origin === stateLegs[index]?.origin &&
        leg.destination === stateLegs[index]?.destination &&
        leg.departureDate === stateLegs[index]?.departureDate,
    );

  // Passenger counts, stops, and fare class always come from the URL handoff when
  // it carries an itinerary — these are plain dispatches (not API calls), so they
  // aren't gated on the route diff; a matching re-entry just re-sets the same
  // values. Fall back to the current store value when a param is absent.
  if (hasUrlItinerary) {
    const existingCounts = getExistingStateVariables(state);
    yield putResolve(
      searchActions.setPassengerCounts({
        adultsCount: parsedQueryString.adultsCount ?? existingCounts.adultsCount,
        childrenCount:
          parsedQueryString.childrenCount ?? existingCounts.childrenCount,
        infantsInSeatCount:
          parsedQueryString.infantsInSeatCount ??
          existingCounts.infantsInSeatCount,
        infantsOnLapCount:
          parsedQueryString.infantsOnLapCount ??
          existingCounts.infantsOnLapCount,
      }),
    );

    yield putResolve(
      searchActions.setStopsOption(
        (parsedQueryString.stopsOption as SliceStopCountFilter) ||
          stopsOptionFromState,
      ),
    );

    const noLCC = parsedQueryString?.noLCC || noLCCFromState;
    const fareClass =
      parsedQueryString?.fareClass != null
        ? typeof parsedQueryString.fareClass === "string"
          ? [parsedQueryString.fareClass]
          : parsedQueryString.fareClass
        : fareClassFromState;
    if (fareClass != null) {
      yield putResolve(
        searchActions.setFareclassOptionFilter({
          basic: fareClass.includes("basic") || false,
          standard: fareClass.includes("standard") || noLCC,
          enhanced: fareClass.includes("enhanced") || noLCC,
          premium: fareClass.includes("premium") || noLCC,
          luxury: fareClass.includes("luxury") || noLCC,
        }),
      );
    }
  }

  let routes = multicityRoutes.map((route) => {
    const { origin, destination, departureDate } = route;
    return {
      departureDate: dayjs(departureDate).format("YYYY-MM-DD"),
      origin: { ...origin?.id.code },
      destination: { ...destination?.id.code },
    };
  });

  // Re-resolve routes from the URL when the URL itinerary differs from state, or
  // state routes are incomplete. This covers a fresh navigation, a reload, and —
  // critically — a second multicity search in the same session where the store
  // still holds the previous itinerary.
  if (
    hasUrlItinerary &&
    (!urlLegsMatchState || !isAllFieldsPopulated(multicityRoutes))
  ) {
    const completedSearchRoutes = [];
    const updatedStateRoutes = [];

    for (const departure of urlLegs) {
      const { origin, destination, departureDate } = departure;
      const {
        correspondingOrigin: originToSearch,
        correspondingDestination: destinationToSearch,
      } = yield fetchOriginDestination(origin, destination);

      completedSearchRoutes.push({
        departureDate,
        origin: { ...originToSearch.id.code },
        destination: { ...destinationToSearch.id.code },
      });

      updatedStateRoutes.push({
        origin: originToSearch,
        destination: destinationToSearch,
        departureDate: dayjs(departureDate).toDate(),
      });
    }

    if (
      updatedStateRoutes.length >= 2 &&
      isAllFieldsPopulated(updatedStateRoutes)
    )
      yield putResolve(searchActions.setAllMulticityRoutes(updatedStateRoutes));

    routes = completedSearchRoutes;
  }

  // ensure the category reflects multicity before we serialize state to the URL
  if (tripCategory !== TripCategory.MULTI_CITY && multicityRoutes.length > 1) {
    yield putResolve(searchActions.setTripCategory(TripCategory.MULTI_CITY));
  }

  // re-read state so the URL is populated from the completed routes + category,
  // rather than the stale snapshot captured before this function ran
  state = yield select();

  const {
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
    stopsOption,
    noLCC,
  } = getExistingStateVariables(state);

  //  If we are missing the data we need from both the state and the query params we should redirect the user home.
  if (
    shouldRedirect({
      multicityRoutes: routes,
      stopsOption,
    })
  ) {
    yield putResolve(actions.resetSelectedTrip());
    yield putResolve(
      searchActions.setMobileSearchProgress(
        MobileFlightSearchStep.LocationSearch,
      ),
    );

    return action.history.push(PATH_HOME);
  }

  yield populateFlightShopMulticityQueryParametersFromState({
    state,
    history,
    isBookingStep:
      history.location.pathname === PATH_BOOK &&
      multicityFlightShopProgress === MulticityFlightShopStep.BookTrip,
    newQueryParams: { multicityFlightShopProgress },
  });

  return {
    routes,
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
    stopsOption,
    noLCC,
  };
}

function* fetchOriginDestination(
  originString: string,
  destinationString: string,
): Generator<
  {},
  {
    correspondingDestination: ITripTerminus;
    correspondingOrigin: ITripTerminus;
  },
  { categories: ICategorizedResponse[] }
> {
  const originRequestBody: ILocationQueryLabel = {
    LocationQuery: LocationQueryEnum.Label,
    l: originString,
  };

  const { categories: originCategories }: IResponse =
    yield fetchLocationAutocomplete(originRequestBody);
  const correspondingOrigin = originCategories
    .flatMap((category) => (category as ITripTerminusCategory).results)
    .find((result) => result.id.code.code === originString) as ITripTerminus;

  const destinationRequestBody: ILocationQueryLabel = {
    LocationQuery: LocationQueryEnum.Label,
    l: destinationString,
  };

  const { categories: destinationCategories }: IResponse =
    yield fetchLocationAutocomplete(destinationRequestBody);
  const correspondingDestination = destinationCategories
    .flatMap((category) => (category as ITripTerminusCategory).results)
    .find(
      (result) => result.id.code.code === destinationString,
    ) as ITripTerminus;

  return { correspondingDestination, correspondingOrigin };
}
